import {
  addEdge,
  applyNodeChanges,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeAddChange,
  type NodeTypes,
  type OnSelectionChangeFunc,
  ReactFlow,
  type ReactFlowInstance,
  useNodesState,
} from "@xyflow/react";
import { find } from "ramda";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ActivityInstance } from "@/graphql.ts";
import { WorkflowConnectionLine } from "@/modules/workflow/components/WorkflowConnectionLine.tsx";
import { WorkflowPreviewEdge } from "@/modules/workflow/components/WorkflowPreviewEdge.tsx";
import { WorkflowPreviewNode } from "@/modules/workflow/components/WorkflowPreviewNode.tsx";
import type { WorkflowConnectorFragment } from "@/modules/workflow/graphql/workflow.gql.ts";
import { useWorkflowPreviewWebSocket } from "@/modules/workflow/hooks/useWorkflowEditorWebSocket.ts";
import { activityToPreviewNode, connectorToEdge } from "@/modules/workflow/utils/generateWorkflow.ts";
import type { Activity, PreviewNodeData } from "@/modules/workflow/utils/types.ts";

export interface WorkflowPreviewProps {
  processorActivities: Activity[];
  workflowActivities: ActivityInstance[];
  workflowConnectors: WorkflowConnectorFragment[];
  onSelect?: OnSelectionChangeFunc;
  eventId?: number;
}

export const WorkflowPreview: FC<WorkflowPreviewProps> = ({
  processorActivities,
  workflowActivities,
  workflowConnectors,
  onSelect,
  eventId,
}) => {
  const nodeTypes = useMemo(() => ({ activity: WorkflowPreviewNode }), []);
  const edgeTypes = useMemo(() => ({ activity: WorkflowPreviewEdge }), []);
  const [edges, setEdges] = useState<Edge[]>([]);
  const previewInstance = useRef<ReactFlowInstance<Node<PreviewNodeData>> | undefined>(undefined);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<PreviewNodeData>>([]);

  const onWebsocketNodeChange = useCallback(
    (changes: { nodeId: string; data: Partial<PreviewNodeData> }[]) => {
      onNodesChange(
        changes.map((change) => ({
          id: change.nodeId,
          type: "replace",
          item: {
            ...nodes.find((node) => node.id === change.nodeId)!,
            data: {
              ...nodes.find((node) => node.id === change.nodeId)!.data,
              status: change.data.status,
            },
          },
        })),
      );
    },
    [nodes, onNodesChange],
  );

  useWorkflowPreviewWebSocket(onWebsocketNodeChange, eventId, [workflowActivities]);

  // build edges and nodes from the combination of the workflow and the list of activities
  useEffect(() => {
    if (processorActivities) {
      // resurrect nodes
      const addNodeChanges = workflowActivities.map((workflowActivity) => {
        const activity = getActivity(workflowActivity.name, workflowActivity.version, processorActivities);
        const node = activityToPreviewNode(workflowActivity, activity);
        return { type: "add", item: node } satisfies NodeAddChange;
      });

      const nodeChanges = applyNodeChanges(addNodeChanges, []);
      setNodes(nodeChanges);

      const disabledNodes = nodeChanges.filter((n) => n.data.disabled);

      // resurrect edges
      workflowConnectors.map((connector) => {
        setEdges((edges) =>
          addEdge(
            connectorToEdge(
              connector,
              disabledNodes.findIndex((n) => n.id === connector.source) > -1 ||
                disabledNodes.findIndex((n) => n.id === connector.target) > -1,
            ),
            edges,
          ),
        );
      });
      previewInstance.current?.fitView({ maxZoom: 1 });
    }
  }, [workflowActivities, workflowConnectors, processorActivities, setNodes]);

  useEffect(() => {
    previewInstance.current?.fitView({ maxZoom: 1 });
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes as unknown as NodeTypes}
      edgeTypes={edgeTypes as unknown as EdgeTypes}
      panOnScroll={false}
      selectionOnDrag={false}
      onInit={(instance) => {
        previewInstance.current = instance;
      }}
      onNodesChange={onNodesChange}
      fitViewOptions={{ padding: 0.2 }}
      zoomOnDoubleClick={false}
      proOptions={{ hideAttribution: true }}
      minZoom={0.25}
      maxZoom={1}
      connectionLineComponent={WorkflowConnectionLine}
      fitView={true}
      elementsSelectable={onSelect != undefined}
      nodesConnectable={false}
      nodesDraggable={false}
      preventScrolling={false}
      zoomOnScroll={false}
      panOnDrag={false}
      onSelectionChange={onSelect}
      multiSelectionKeyCode={null}
    />
  );
};

/* Same as the Workflow Editor store function */
const getActivity = (activityName: string, version: string, activities: Activity[]): Activity | undefined =>
  find((activity) => activity.name === activityName && activity.version.name === version, activities);
